import React, { useRef, useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { FieldError, NoteField } from './dialog_parts';
import { KIND, error_view } from './error_handlers';
import { bulk_result_view, selection_total_text } from './adapters';
import { bulk_chunks } from './requests';
import { bulk_form_errors } from './form_errors';
import { BULK_ACTION, ERROR, reason_options } from './params';

/* bulk waive / bulk "use a free pass". */
const TITLES = {
  [BULK_ACTION.WAIVE]: 'Waive selected penalties',
  [BULK_ACTION.APPLY_FREE_PASS]: 'Use Free Passes on the selected days'
};

const BulkModal = ({
  action,
  rows,
  skipped,
  send,
  on_finished,
  on_permission,
  on_close,
  on_focus_lost
}) => {
  const [form, setForm] = useState({ reason_code: '', note: '', approved_by: '' });
  const [touched, setTouched] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [groups, setGroups] = useState([]);
  const [failure, setFailure] = useState('');
  const [stopping, setStopping] = useState(false);
  const stop = useRef(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const errors = bulk_form_errors(action, form);
  const by_uuid = Object.fromEntries(rows.map((r) => [r.uuid, r]));
  const result = bulk_result_view(groups, by_uuid);
  const sent_any = groups.length > 0 || !!failure;
  const displaced = groups.filter((g) => g.code === ERROR.CONFIRM_DISPLACE_REQUIRED);

  const run = async (target_rows, confirm_displace) => {
    const chunks = bulk_chunks(target_rows.map((r) => r.row));
    stop.current = false;
    setStopping(false);
    setRunning(true);
    setFailure('');
    const landed = [];
    let done = 0;
    for (let i = 0; i < chunks.length; i += 1) {
      if (stop.current) break;
      setProgress({ done, total: chunks.length });
      try {
        // Sequential on purpose: each chunk waits on month locks and recomputes; the server wants no overlap.
        // eslint-disable-next-line no-await-in-loop
        const data = await send(chunks[i], form, confirm_displace);
        landed.push(...(Array.isArray(data.groups) ? data.groups : []));
        done += 1;
      } catch (e) {
        const view = error_view(e, { write: true });
        if (view.kind === KIND.PERMISSION) {
          on_permission(e);
          return;
        }
        setFailure(`Batch ${done + 1} of ${chunks.length}: ${view.text}`);
        break;
      }
    }
    setGroups((g) =>
      g
        .filter(
          (x) =>
            !landed.some((l) => l.teacher_uuid === x.teacher_uuid && l.pay_month === x.pay_month)
        )
        .concat(landed)
    );
    setProgress({ done, total: chunks.length, stopped: stop.current && done < chunks.length });
    setRunning(false);
    on_finished();
  };

  const submit = () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean) || !rows.length) return;
    run(rows, false);
  };

  const resend_displaced = () => {
    const uuids = new Set(displaced.flatMap((g) => (g.items || []).map((i) => i.penalty_uuid)));
    run(
      rows.filter((r) => uuids.has(r.uuid)),
      true
    );
  };

  return (
    <TpDialog
      title_id="tp-pe-bulk-title"
      title={TITLES[action]}
      size="md"
      busy={running}
      guard_text={sent_any ? '' : discard_guard_text(form.note, form.approved_by)}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-modal tp-modal-wide">
        <div className="tp-ctx">
          {rows.length} {rows.length === 1 ? 'penalty' : 'penalties'} · charged now{' '}
          {selection_total_text(rows)}
          {skipped.length ? (
            <div>
              {skipped.length} selected {skipped.length === 1 ? 'penalty is' : 'penalties are'} left
              out:{' '}
              {action === BULK_ACTION.WAIVE
                ? 'they cannot be waived'
                : 'a Free Pass cannot be used on them'}{' '}
              ({skipped.map((r) => r.short_id).join(', ')}).
            </div>
          ) : null}
        </div>
        {action === BULK_ACTION.APPLY_FREE_PASS ? (
          <div className="tp-hint">
            Each teacher gets one pass per selected day. A pass covers the selected penalties on its
            day and every 1 to 24 hour cancellation or replacement there. A teacher without enough
            passes for all their days is left unchanged.
          </div>
        ) : (
          <div className="tp-hint">
            Each penalty is waived in full and logged separately. A teacher's penalties are saved
            together or not at all.
          </div>
        )}

        {sent_any ? null : (
          <>
            <div className="tp-field">
              <label htmlFor="tp-pe-bulk-reason">Reason</label>
              <select
                id="tp-pe-bulk-reason"
                className="tp-select"
                value={form.reason_code}
                onChange={(e) => set('reason_code', e.target.value)}
              >
                <option value="">Choose a reason</option>
                {reason_options('waive').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <FieldError text={touched && errors.reason_code} />
            </div>
            {action === BULK_ACTION.WAIVE ? (
              <div className="tp-field">
                <label htmlFor="tp-pe-bulk-approved">Approved by (optional)</label>
                <input
                  id="tp-pe-bulk-approved"
                  className="tp-input"
                  value={form.approved_by}
                  onChange={(e) => set('approved_by', e.target.value)}
                />
                <FieldError text={touched && errors.approved_by} />
              </div>
            ) : null}
            <NoteField
              id="tp-pe-bulk-note"
              label="Note (internal, required)"
              value={form.note}
              on_change={(v) => set('note', v)}
              error_text={touched && errors.note}
            />
          </>
        )}

        {running && progress ? (
          <div className="tp-banner tp-banner-info" role="status">
            Sending batch {progress.done + 1} of {progress.total}…
          </div>
        ) : null}
        {!running && progress && progress.stopped ? (
          <div className="tp-banner tp-banner-warn" role="status">
            Stopped after {progress.done} of {progress.total} batches. The rest were not sent.
          </div>
        ) : null}
        {failure ? (
          <div className="tp-banner tp-banner-error" role="alert">
            {failure} Later batches were not sent.
          </div>
        ) : null}
        {result.lines.length ? (
          <div role="status">
            <div className="tp-card-field-label">
              Saved for {result.applied_groups} teacher-
              {result.applied_groups === 1 ? 'month' : 'months'}; refused for{' '}
              {result.refused_groups}.
            </div>
            <ul className="tp-dup-list">
              {result.lines.map((l) => (
                <li key={l.key} className={l.applied ? '' : 'tp-neg'}>
                  {l.teacher_name} · {l.month_text}: {l.text}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="tp-modal-ft">
          {running ? (
            <button
              type="button"
              className="tp-btn-ghost"
              onClick={() => {
                stop.current = true;
                setStopping(true);
              }}
            >
              {stopping ? 'Stopping after this batch…' : 'Stop after this batch'}
            </button>
          ) : (
            <button type="button" className="tp-btn-ghost" onClick={on_close}>
              {sent_any ? 'Close' : 'Cancel'}
            </button>
          )}
          {!sent_any ? (
            <button
              type="button"
              className="tp-btn"
              onClick={running || !rows.length ? undefined : submit}
              aria-disabled={running || !rows.length ? 'true' : undefined}
            >
              {running
                ? 'Saving…'
                : action === BULK_ACTION.WAIVE
                  ? `Waive ${rows.length}`
                  : `Use Free Passes for ${rows.length}`}
            </button>
          ) : null}
          {!running && displaced.length ? (
            <button type="button" className="tp-btn" onClick={resend_displaced}>
              Use them anyway for {displaced.length}{' '}
              {displaced.length === 1 ? 'teacher' : 'teachers'}
            </button>
          ) : null}
        </div>
      </div>
    </TpDialog>
  );
};

export default BulkModal;
